import { useEffect } from 'react';
import get from 'lodash/get';
import { useSelector, useDispatch } from 'react-redux';
import { doLogout } from 'store/actions/auth';
import { store } from 'store';

let timeOutLogout = null;
export const lastActivityKey = 'lastActivity';
export const expirationKey = 'expiresAt';

const useInactivity = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(({ auth }) => !!auth?.auth_token);
  const timeForLogout =
    1000 * 60 * parseInt(import.meta.env.VITE_REFRESH_TTL || 60);

  // setting lockscreen timeout to trigger lockscreen after lockscreen time
  const setupInactivityTimeout = () => {
    timeOutLogout = setTimeout(() => {
      // showing lockscreen once time out is over
      dispatch(doLogout());
    }, timeForLogout);
  };

  // used to clear timeouts on activity made from user
  const clearLogoutTimeout = () => {
    // clearing both timeouts
    clearTimeout(timeOutLogout);
  };

  // used to reset timeouts on activity made from user
  const resetInactivityTimeout = () => {
    // clearing timeouts first
    clearLogoutTimeout();

    const currentTime = new Date().getTime();
    // setting the current time in localstorage so we can check when last activity was made
    localStorage.setItem(lastActivityKey, currentTime);

    const state = store.getState();
    const isCurrentLoggedIn = !!get(state, 'auth.auth_token', null);
    // checking if user is logged in then setting up the inactivity timer again
    if (isCurrentLoggedIn) {
      setupInactivityTimeout();
    }
  };

  useEffect(() => {
    // adding all the event listeners for document so users events are monitored
    // so we can check for inactivity of user from there and logout them

    document.addEventListener('mousemove', () => {
      resetInactivityTimeout();
    });
    document.addEventListener('click', () => {
      resetInactivityTimeout();
    });
    document.addEventListener(
      'scroll',
      () => {
        resetInactivityTimeout();
      },
      true
    );
    document.addEventListener('resize', () => {
      resetInactivityTimeout();
    });
    document.addEventListener('keydown', () => {
      resetInactivityTimeout();
    });

    // this will monitor the token across the tabs
    window.onstorage = function(e) {
      // whenever user activity key is updated the item will be updated across the tabs
      if (e.key === lastActivityKey) {
        const sharedInactivityTime = localStorage.getItem(lastActivityKey);
        const isCurrentLoggedIn = !!get(
          store.getState(),
          'auth.auth_token',
          null
        );
        if (sharedInactivityTime && isCurrentLoggedIn) {
          // if we get the new value for the key then we are clearing timers and setting them again
          clearLogoutTimeout();
          setupInactivityTimeout();
        } else if (timeOutLogout) {
          clearLogoutTimeout();
        }
      }
    };
    return () => {
      document.removeEventListener('mousemove', () => {});
      document.removeEventListener('click', () => {});
      document.removeEventListener('scroll', () => {});
      document.removeEventListener('resize', () => {});
      document.removeEventListener('keydown', () => {});
    };
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    // initially when timer is not set then we are setting user time
    if (!timeOutLogout && isLoggedIn) {
      setupInactivityTimeout();
    }
    return () => {
      clearLogoutTimeout();
    };
    // eslint-disable-next-line
  }, [isLoggedIn]);
};

export default useInactivity;
